import { computed, makeObservable } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { DisplayModel } from '@feathr/rachis';

import type { Campaign } from './campaigns';
import { CampaignClass } from './campaigns';

// =============================================================================
// Dimension Definitions
// =============================================================================

export interface IDimensionDefinition {
  name: string;
  spec: string;
  width: number;
  height: number;
}

export const ValidDimensions: Record<string, IDimensionDefinition> = {
  '728x90': {
    name: 'Leaderboard',
    spec: 'leaderboard',
    width: 728,
    height: 90,
  },
  '300x50': {
    name: 'Mini-Leaderboard',
    spec: 'mini_leaderboard',
    width: 300,
    height: 50,
  },
  '300x250': {
    name: 'Medium Rectangle',
    spec: 'medium_rectangle',
    width: 300,
    height: 250,
  },
  '160x600': {
    name: 'Wide Skyscraper',
    spec: 'wide_skyscraper',
    width: 160,
    height: 600,
  },
  '300x600': {
    name: 'Filmstrip',
    spec: 'filmstrip',
    width: 300,
    height: 600,
  },
  '300x1050': {
    name: 'Portrait',
    spec: 'portrait',
    width: 300,
    height: 1050,
  },
  '970x90': {
    name: 'Pushdown',
    spec: 'pushdown',
    width: 970,
    height: 90,
  },
  '970x250': {
    name: 'Billboard',
    spec: 'billboard',
    width: 970,
    height: 250,
  },
  '320x50': {
    name: 'Pull',
    spec: 'pull',
    width: 320,
    height: 50,
  },
  '320x250': {
    name: 'Tablet Leaderboard',
    spec: 'tablet_leaderboard',
    width: 320,
    height: 250,
  },
  '320x480': {
    name: 'Mobile Interstitial',
    spec: 'mobile_interstitial',
    width: 320,
    height: 480,
  },
  '120x240': {
    name: 'Vertical Banner',
    spec: 'vertical_banner',
    width: 120,
    height: 240,
  },
  '120x600': {
    name: 'Skyscraper',
    spec: 'skyscraper',
    width: 120,
    height: 600,
  },
  '125x125': {
    name: 'Button',
    spec: 'button',
    width: 125,
    height: 125,
  },
  '200x200': {
    name: 'Small Square',
    spec: 'small_square',
    width: 200,
    height: 200,
  },
  '234x60': {
    name: 'Half Banner',
    spec: 'half_banner',
    width: 234,
    height: 60,
  },
  '120x20': {
    name: 'Feature Phone Small',
    spec: 'feature_phone_small',
    width: 120,
    height: 20,
  },
  '168x28': {
    name: 'Feature Phone Medium',
    spec: 'feature_phone_medium',
    width: 168,
    height: 28,
  },
  '216x36': {
    name: 'Feature Phone Large',
    spec: 'feature_phone_small',
    width: 216,
    height: 36,
  },
  '240x400': {
    name: 'Vertical Rectangle',
    spec: 'vertical_rectangle',
    width: 240,
    height: 400,
  },
  '250x250': {
    name: 'Square',
    spec: 'square',
    width: 250,
    height: 250,
  },
  '250x360': {
    name: 'Triple Widescreen',
    spec: 'triple_widescreen',
    width: 250,
    height: 360,
  },
  '320x100': {
    name: 'Mobile Pull',
    spec: 'mobile_pull',
    width: 320,
    height: 100,
  },
  '336x280': {
    name: 'Large Rectangle',
    spec: 'large_rectangle',
    width: 336,
    height: 280,
  },
  '468x60': {
    name: 'Banner',
    spec: 'banner',
    width: 468,
    height: 60,
  },
  '180x150': {
    name: 'Small Rectangle',
    spec: 'small_rectangle',
    width: 180,
    height: 150,
  },
  '0x0': {
    name: 'Video',
    spec: 'video',
    width: 0,
    height: 0,
  },
  '1280x720': {
    name: '720p HD Video',
    spec: '720p_video',
    width: 1280,
    height: 720,
  },
  '1920x1080': {
    name: '1080p HD Video',
    spec: '1080p_video',
    width: 1920,
    height: 1080,
  },
  '480x360': {
    name: '360p SD Video',
    spec: '360p_video',
    width: 480,
    height: 360,
  },
  '640x480': {
    name: '480p SD Video',
    spec: '480p_video',
    width: 640,
    height: 480,
  },
};

// =============================================================================
// Enums and Constants
// =============================================================================

export enum CreativeClass {
  DisplayAdTag = 'Creative.DisplayCreative.AdTagCreative.AdTagRTBCreative',
  DisplayBannersnack = 'Creative.DisplayCreative.BannersnackRTBCreative',
  DisplayImage = 'Creative.DisplayCreative.ImageCreative.ImageRTBCreative',
  DisplayVideo = 'Creative.DisplayCreative.VideoCreative.VideoRTBCreative',
  FacebookImage = 'Creative.DisplayCreative.FacebookRTBCreative.FacebookImageRTBCreative',
  FacebookVideo = 'Creative.DisplayCreative.FacebookRTBCreative.FacebookVideoRTBCreative',
  PartnerMessageEmail = 'Creative.HtmlCreative.EmailCreative.PartnerMessageCreative',
  ReferralBanner = 'Creative.HtmlCreative.PageCreative.ReferralBannerCreative',
  ReferralEmail = 'Creative.HtmlCreative.EmailCreative.ReferralEmailCreative',
  ReferralPage = 'Creative.HtmlCreative.PageCreative.ReferralPageCreative',
  GoogleAd = 'Creative.DisplayCreative.GoogleAdsCreative.GoogleAdsTextCreative',
}

export type TAdGroup = 'main' | 'lookalike';

// =============================================================================
// Base Creative Interface
// =============================================================================

export interface ICreativeAttributes extends IBaseAttributes {
  readonly id: string;
  readonly _cls: CreativeClass;
  readonly account: string;
  readonly date_created: string;
  readonly date_last_modified: string;
  readonly event: string;
  readonly parent: string;
  readonly template: string;
  readonly version: number;
  ad_groups: TAdGroup[] | null;
  destination_url?: string;
  height?: number;
  is_enabled: boolean;
  mimetype: string;
  name: string;
  spec: string;
  stats_cache?: {
    num_clicks?: number;
    num_users_active?: number;
    num_views?: number;
  };
  width?: number;
  alt_text?: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

export function creativeTypeLabel(type: CreativeClass): string {
  const typeMap: Record<CreativeClass, string> = {
    [CreativeClass.DisplayAdTag]: 'Ad Tag Creative',
    [CreativeClass.DisplayBannersnack]: 'Banner Builder',
    [CreativeClass.DisplayImage]: 'Image Creative',
    [CreativeClass.DisplayVideo]: 'Video Creative',
    [CreativeClass.FacebookImage]: 'Facebook Image Creative',
    [CreativeClass.FacebookVideo]: 'Facebook Video Creative',
    [CreativeClass.PartnerMessageEmail]: '-',
    [CreativeClass.ReferralBanner]: 'Banner',
    [CreativeClass.ReferralEmail]: 'Email',
    [CreativeClass.ReferralPage]: 'Page',
    [CreativeClass.GoogleAd]: 'Google Ads',
  };
  return typeMap[type];
}

export function getAspectRatioNotation(ratio: number): string {
  if (Math.abs(ratio - 1.778) < 0.01) {
    return '16:9';
  }
  if (Math.abs(ratio - 3.2) < 0.01) {
    return '16:5';
  }
  if (Math.abs(ratio - 1) < 0.01) {
    return '1:1';
  }
  if (Math.abs(ratio - 0.8) < 0.01) {
    return '4:5';
  }
  if (Math.abs(ratio - 0.563) < 0.01) {
    return '9:16';
  }
  if (Math.abs(ratio - 1.91) < 0.01) {
    return '1.91:1';
  }
  if (Math.abs(ratio - 1.2) < 0.01) {
    return '6:5';
  }
  if (Math.abs(ratio - 0.2) < 0.01) {
    return '1:5';
  }
  if (Math.abs(ratio - 0.5) < 0.01) {
    return '1:2';
  }
  if (Math.abs(ratio - 0.6) < 0.01) {
    return '3:5';
  }
  if (Math.abs(ratio - 0.75) < 0.01) {
    return '4:3';
  }
  if (Math.abs(ratio - 0.4) < 0.01) {
    return '5:4';
  }
  if (Math.abs(ratio - 0.67) < 0.01) {
    return '2:3';
  }

  if (Number.isInteger(ratio)) {
    return `${ratio}:1`;
  } else if (Math.abs(ratio * 10 - Math.round(ratio * 10)) < 0.001) {
    return `${ratio.toFixed(1).replace(/\.0$/, '')}:1`;
  } else {
    return `${ratio.toFixed(2)}:1`;
  }
}

/**
 * Creates a dimension validator for display creatives (banner ads, video ads, etc.).
 *
 * Requires EXACT dimension match - display ad networks have strict size requirements
 * for ad placements, so a 300x250 spec only accepts exactly 300x250 pixels.
 *
 * Note: For Meta/Facebook creatives which allow larger images with matching aspect ratios,
 * use createMetaDimensionValidator from creatives_meta.ts instead.
 *
 * @param dimensions - The dimension definitions to validate against (e.g., ValidDimensions)
 * @param attribute - Which attribute to validate ('width' or 'height')
 * @returns A validator function for use in model constraints
 *
 * @example
 * // In DisplayImageCreative constraints:
 * width: {
 *   equality: createDimensionValidator(ValidDimensions, 'width'),
 * }
 */
export function createDimensionValidator(
  dimensions: Record<string, IDimensionDefinition>,
  attribute: 'width' | 'height',
) {
  return (...args: any[]) => {
    const attributes = args[1];
    const { spec } = attributes;

    if (!spec) {
      return undefined;
    }

    const dimensionDef = Object.values(dimensions).find((d) => d.spec === spec);
    if (!dimensionDef) {
      return undefined;
    }

    return {
      attribute,
      message: `^${attributes.width}x${attributes.height} is not a valid creative size for this type`,
      comparator: (value: number) => value === dimensionDef[attribute],
    };
  };
}

// =============================================================================
// Facebook Dimensions
// =============================================================================

export const ValidFacebookDimensions: Record<string, IDimensionDefinition> = {
  '1200x628': {
    name: 'Facebook Standard',
    spec: 'facebook_standard',
    width: 1200,
    height: 628,
  },
  '1080x1080': {
    name: 'Facebook Standard Square',
    spec: 'facebook_standard_square',
    width: 1080,
    height: 1080,
  },
  '1080x1920': {
    name: 'Facebook Full Portrait Image',
    spec: 'facebook_full_portrait_image',
    width: 1080,
    height: 1920,
  },
  '1920x1080': {
    name: 'Facebook Landscape Image',
    spec: 'facebook_landscape_image',
    width: 1920,
    height: 1080,
  },
  '1080x1350': {
    name: 'Facebook Vertical Image',
    spec: 'facebook_vertical_image',
    width: 1080,
    height: 1350,
  },
};

// =============================================================================
// Creative Base Class
// =============================================================================

/**
 * Creative is the abstract base class for all creative types.
 * It provides common functionality for managing creative assets.
 */
export abstract class Creative<
  T extends ICreativeAttributes = ICreativeAttributes,
> extends DisplayModel<T> {
  public get constraints(): TConstraints<T> {
    return {} as TConstraints<T>;
  }

  constructor(attributes: Partial<T>) {
    super(attributes);

    makeObservable(this);
  }

  @computed
  public get name(): string {
    return this.get('name', '' as Exclude<T['name'], undefined>).trim() || 'Unnamed Creative';
  }

  @computed
  public get isFacebook(): boolean {
    return [CreativeClass.FacebookImage, CreativeClass.FacebookVideo].includes(this.get('_cls'));
  }

  public override getDefaults(): Partial<T> {
    return {
      ad_groups: [] as TAdGroup[],
    } as Partial<T>;
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/projects/${this.get('event')}/content/creatives/${this.id}`, pathSuffix);
  }

  public getSpec(): IDimensionDefinition | undefined {
    const key =
      Object.keys(ValidDimensions).find(
        (dimensionKey) => ValidDimensions[dimensionKey].spec === this.get('spec'),
      ) ?? `${this.get('width')}x${this.get('height')}`;
    if (ValidDimensions.hasOwnProperty(key)) {
      return ValidDimensions[key];
    }
    return undefined;
  }

  public getTypeLabel(): string {
    const spec = this.getSpec();
    if (spec) {
      return spec.name;
    }
    return creativeTypeLabel(this.get('_cls'));
  }

  public getValidDimensionsByCreativeClass(
    _cls: CreativeClass,
  ): Record<string, IDimensionDefinition> {
    const map: Partial<Record<CreativeClass, Record<string, IDimensionDefinition>>> = {
      [CreativeClass.DisplayAdTag]: ValidDimensions,
      [CreativeClass.DisplayBannersnack]: ValidDimensions,
      [CreativeClass.DisplayImage]: Object.fromEntries(
        Object.entries(ValidDimensions).filter(([, value]) => !value.spec.includes('video')),
      ),
      [CreativeClass.DisplayVideo]: Object.fromEntries(
        Object.entries(ValidDimensions).filter(([, value]) => value.spec.includes('video')),
      ),
      [CreativeClass.FacebookImage]: ValidFacebookDimensions,
      [CreativeClass.FacebookVideo]: ValidFacebookDimensions,
    };

    return map[_cls] ?? ValidDimensions;
  }

  public getValidDimensions(campaign: Campaign): Record<string, IDimensionDefinition> {
    const creativeClass = this.get('_cls');

    // These have a fixed "text/html" mimetype so isVideo would always be false,
    // but they should return all dimensions including video specs
    if (
      creativeClass === CreativeClass.DisplayBannersnack ||
      creativeClass === CreativeClass.DisplayAdTag
    ) {
      return ValidDimensions;
    }

    const isVideo = this.get('mimetype')?.includes('video');
    if (campaign.get('_cls') === CampaignClass.MobileGeoFencing) {
      const mobileDimensions = new Set(['300x250', '320x50', '300x50', '336x280', '320x480']);
      return Object.fromEntries(
        Object.entries(ValidDimensions).filter(([key]) => mobileDimensions.has(key)),
      );
    }
    return Object.fromEntries(
      Object.entries(ValidDimensions).filter(([, value]) =>
        isVideo ? value.spec.includes('video') : !value.spec.includes('video'),
      ),
    );
  }
}

/**
 * BaseCreative is the fallback creative class for unknown or unspecified creative types.
 */
export class BaseCreative extends Creative<ICreativeAttributes> {
  public readonly className = 'BaseCreative';

  public override get constraints(): TConstraints<ICreativeAttributes> {
    return super.constraints;
  }
}
